<template>
  <div
    :class="[
      'sidebar_item justify-between',
      {
        sidebar_item_selected: isSelected,
        'disabled cursor-not-allowed': isDisabled,
        'p-[10px]': !isTooltip,
      },
    ]"
  >
    <div class="flex flex-row gap-2 items-center min-w-0">
      <template v-if="!hideIcon && !isTooltip">
        <div v-if="!isExpanded && showIndicator && unreadCount > 0" class="relative size-5 flex-shrink-0">
          <ThemeImage
            v-if="option?.icon"
            class="size-5"
            :icon="option.icon"
            :alt="`${option.text.toLowerCase()}_icon`"
          />
          <img
            v-else-if="isIconUrl"
            class="size-5 rounded object-cover"
            :src="isSelected ? option.selected : option.unselected"
            :alt="`${option.text.toLowerCase()}_icon`"
          />
          <ThemeImage
            v-else-if="option?.selected && option?.unselected"
            class="size-5"
            :icon="isSelected ? option.selected : option.unselected"
            :alt="`${option.text.toLowerCase()}_icon`"
          />
          <div
            class="absolute -top-0.5 -right-0.5 flex items-center justify-center text-[10px] text-white font-semibold leading-[14px] min-w-[14px] h-[14px] count_tag"
          >
            {{ unreadCount }}
          </div>
        </div>
        <ThemeImage
          v-else-if="option?.icon"
          class="size-5 flex-shrink-0"
          :icon="option.icon"
          :alt="`${option.text.toLowerCase()}_icon`"
        />
        <img
          v-else-if="isIconUrl"
          class="size-5 flex-shrink-0 rounded object-cover"
          :src="isSelected ? option.selected : option.unselected"
          :alt="`${option.text.toLowerCase()}_icon`"
        />
        <ThemeImage
          v-else-if="option?.selected && option?.unselected"
          class="size-5 flex-shrink-0"
          :icon="isSelected ? option.selected : option.unselected"
          :alt="`${option.text.toLowerCase()}_icon`"
        />
      </template>

      <div class="flex items-baseline gap-1 min-w-0 flex-1">
        <a-tooltip
          v-if="isExpanded && shouldShowTooltip"
          placement="top"
          color="var(--tooltip-bg)"
        >
          <template #title>
            <span class="text_color">{{ option.text }}</span>
          </template>
          <span
            ref="textRef"
            :class="[
              'text-sm font-normal text_color truncate block',
              { 'opacity-80': !isSelected },
            ]"
          >
            {{ option.text }}
          </span>
        </a-tooltip>

        <span
          v-else
          ref="textRef"
          :class="[
            'text-sm font-normal text_color truncate block',
            { 'opacity-80': !isSelected },
          ]"
        >
          {{ option.text }}
        </span>

        <span
          v-if="isExpanded && isDisabled"
          :class="[
            'truncate text_color flex-shrink-0',
            isTooltip ? 'text-xs' : 'opacity-40 text-[11px]',
          ]"
        >
          (Coming soon)
        </span>
      </div>
    </div>

    <div v-if="isExpanded" class="flex items-center gap-1 flex-shrink-0">
      <div
        v-if="showIndicator && unreadCount > 0"
        class="flex items-center justify-center text-xs text-white font-semibold pt-[1px] px-[4px] leading-[14px] !min-w-[16px] count_tag"
      >
        {{ unreadCount }}
      </div>
      
      <SidebarAppActionsDropdown
        v-if="appId"
        :app-id="appId"
        :app-name="appName"
        :placement="dropdownPlacement"
        :always-visible="alwaysVisibleDropdown"
        @open-change="(open) => $emit('dropdown-open-change', open)"
        @mode-change="(mode) => $emit('dropdown-mode-change', mode)"
      />

      <ThemeImage
        v-if="showArrow"
        class="size-4 transition-transform duration-300"
        :class="{ 'rotate-90': isOpen }"
        :icon="imageNames.RIGHT_ARROW_SMALL_GREY"
        alt="right_arrow_small"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import SidebarAppActionsDropdown from "@/src/components/sidebar_components/SidebarAppActionsDropdown.vue";

const props = defineProps({
  option: { type: Object, required: true },
  isExpanded: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  showArrow: { type: Boolean, default: false },
  isDisabled: { type: Boolean, default: false },
  isTooltip: { type: Boolean, default: false },
  isOpen: { type: Boolean, default: false },
  hideIcon: { type: Boolean, default: false },
  appId: { type: Number, default: null },
  appName: { type: String, default: null },
  dropdownPlacement: { type: String, default: "bottomLeft" },
  alwaysVisibleDropdown: { type: Boolean, default: false },
  showIndicator: { type: Boolean, default: false },
  unreadCount: { type: Number, default: 0 },
});

defineEmits(["dropdown-open-change", "dropdown-mode-change"]);

const isIconUrl = computed(() => {
  const icon = props.option?.selected || props.option?.unselected;
  return icon && (icon.startsWith("http://") || icon.startsWith("https://"));
});

const textRef = ref(null);
const shouldShowTooltip = ref(false);
let resizeObserver = null;

const checkTextTruncation = () => {
  if (!props.isExpanded || !textRef.value) {
    shouldShowTooltip.value = false;
    return;
  }

  const isTruncated = textRef.value.scrollWidth > textRef.value.clientWidth;
  shouldShowTooltip.value = isTruncated;
};

onMounted(() => {
  nextTick(() => {
    checkTextTruncation();

    // Use ResizeObserver to detect width changes (e.g., dropdown opening)
    if (textRef.value && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        checkTextTruncation();
      });
      resizeObserver.observe(textRef.value);
    }
  });
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

// Watch for changes that affect truncation
watch(
  () => [props.option.text, props.isExpanded],
  () => {
    nextTick(() => {
      checkTextTruncation();
    });
  }
);
</script>

